/**
 * Ramener n'importe quelle erreur réseau à une phrase affichable.
 *
 * ## Pourquoi ce fichier existe
 *
 * L'écran de l'Optimiseur de flux est devenu **entièrement blanc** à la fin d'une génération
 * par lot. La console disait :
 *
 * ```
 * POST …/generate-suggestions 502 (Bad Gateway)
 * Uncaught Error: Minified React error #31 … object with keys {code, message}
 * ```
 *
 * `setError(err.response?.data?.error || 'Erreur …')` suppose que le corps d'erreur porte une
 * **chaîne** sous `error`. C'est vrai de nos routes, qui n'en renvoient jamais d'autre — et
 * faux de la passerelle, qui répond `{ code, message }` quand elle n'a pas pu joindre
 * l'application. L'objet atteignait `<span>{error}</span>`, et React refuse un objet comme
 * enfant : il démonte l'arbre entier.
 *
 * ⚠️ **Le défaut n'est pas dans la route, il est dans la confiance.** Tant qu'une erreur vient
 * de notre serveur, sa forme est connue ; dès qu'elle vient d'un intermédiaire — passerelle,
 * proxy d'entreprise, portail Wi-Fi — elle ne l'est plus. Un écran ne doit jamais rendre
 * directement ce qu'il n'a pas composé.
 *
 * ⚠️ **Et un plantage de rendu est le pire des symptômes** : il efface aussi le message qui
 * aurait expliqué la panne. On perd l'erreur **et** le moyen de la lire.
 *
 * @module utils/messageErreur
 */

/** Ce que la passerelle dit, quand elle le dit. */
const PHRASES_PASSERELLE = {
  502: 'Le serveur n\'a pas répondu à temps.',
  503: 'Le service est momentanément indisponible.',
  504: 'Le serveur a mis trop de temps à répondre.'
};

/**
 * Une phrase, à partir de ce qu'une erreur Axios porte réellement.
 *
 * @param {*} erreur   l'objet attrapé — Axios, `Error`, ou n'importe quoi
 * @param {string} repli  la phrase à servir quand rien d'exploitable n'est trouvé
 * @returns {string} toujours une chaîne, jamais un objet
 */
export function messageErreur(erreur, repli = 'Une erreur est survenue.') {
  const corps = erreur?.response?.data;
  const statut = erreur?.response?.status;

  /*
   * L'ordre va du plus précis au plus général. `corps.error` d'abord, parce que c'est la forme
   * de nos routes et la seule qui nomme le geste à faire — « Maximum 50 produits par
   * requête » vaut mieux que « Le serveur n'a pas répondu ».
   */
  for (const candidat of [corps?.error, corps?.message, corps?.hint, corps]) {
    if (typeof candidat === 'string' && candidat.trim()) {
      /*
       * ⚠️ **Une page HTML n'est pas un message.** Beaucoup de passerelles répondent une page
       * entière : l'afficher déverserait des balises dans le bandeau. On la reconnaît à son
       * ouverture, et on lui préfère la phrase du statut.
       */
      if (/^\s*<(!doctype|html)/i.test(candidat)) break;
      return candidat.trim();
    }
    /*
     * ⚠️ **Le cas qui a blanchi l'écran** : `{ code, message }`. On va chercher le `message`
     * dedans plutôt que de rendre l'objet — et s'il n'y en a pas, on tombera sur le repli.
     */
    if (candidat && typeof candidat === 'object' && typeof candidat.message === 'string'
      && candidat.message.trim()) {
      return candidat.message.trim();
    }
  }

  if (PHRASES_PASSERELLE[statut]) return PHRASES_PASSERELLE[statut];

  /*
   * ⚠️ **Un délai dépassé côté navigateur n'a ni réponse ni statut**, et son `message` natif
   * est « timeout of 0ms exceeded » — illisible. Même soin que `message_transport` côté
   * Python, pour la même raison : une panne sans nom se prend pour un bug de l'application.
   */
  if (erreur?.code === 'ECONNABORTED' || erreur?.code === 'ETIMEDOUT') {
    return 'La requête a dépassé le délai d\'attente.';
  }
  if (erreur?.code === 'ERR_NETWORK') return 'Le serveur est injoignable.';

  if (typeof erreur?.message === 'string' && erreur.message.trim()
    && !/^Request failed with status code/.test(erreur.message)) {
    return erreur.message.trim();
  }

  return statut ? `${repli} (erreur ${statut})` : repli;
}

/**
 * Un garde-fou de rendu : ce qui atteint le JSX est une chaîne, quoi qu'il arrive.
 *
 * ⚠️ **Il double `messageErreur`, et c'est voulu.** Celui-ci couvre les appelants qui posent
 * un message sans passer par la normalisation — il y en a vingt-trois dans le seul
 * Optimiseur de flux, et un seul oubli suffit à blanchir l'écran. Le filet coûte une
 * ligne ; le défaut coûte l'écran entier.
 */
export function texteAffichable(valeur) {
  if (valeur === null || valeur === undefined) return null;
  if (typeof valeur === 'string') return valeur;
  if (typeof valeur === 'number' || typeof valeur === 'boolean') return String(valeur);
  if (typeof valeur === 'object' && typeof valeur.message === 'string') return valeur.message;
  // Dernier recours : on dit qu'il y a eu une erreur, sans déverser un objet dans le bandeau.
  return 'Une erreur est survenue.';
}
